import { useState } from 'react';
import { useT } from '../i18n/i18n';
import { TARGET_STATUS_LABEL, createTarget, updateTarget, type Target, type TargetStatus } from './api';
import { orNull } from './GoalDates';
import DateField from '../components/DateField';

export default function TargetEditor({ learnerId, goalId, target, onSaved, onCancel, onDelete }: {
  learnerId: string; goalId: string; target: Target | null; onSaved: () => void; onCancel: () => void; onDelete?: () => void;
}) {
  const t = useT();
  const [statement, setStatement] = useState(target?.statement ?? '');
  const [baseline, setBaseline] = useState(target?.baseline ?? '');
  const [success, setSuccess] = useState(target?.successCriterion ?? '');
  const [startDate, setStartDate] = useState(target?.startDate ?? '');
  const [byDate, setByDate] = useState(target?.byDate ?? '');
  const [reviewDate, setReviewDate] = useState(target?.reviewDate ?? '');
  const [what, setWhat] = useState(target?.provisionWhat ?? '');
  const [who, setWho] = useState(target?.provisionWho ?? '');
  const [howOften, setHowOften] = useState(target?.provisionHowOften ?? '');
  const [minutes, setMinutes] = useState(target?.provisionMinutes != null ? String(target.provisionMinutes) : '');
  const [status, setStatus] = useState<TargetStatus>(target?.status ?? 'WORKING');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = target?.id ?? `new-${goalId}`;

  async function save() {
    setBusy(true);
    setError(null);
    const mins = Number.parseInt(minutes, 10);
    const body = {
      statement: statement.trim(), baseline: orNull(baseline), successCriterion: orNull(success),
      startDate: startDate || null, byDate: byDate || null, reviewDate: reviewDate || null, provisionWhat: orNull(what), provisionWho: orNull(who),
      provisionHowOften: orNull(howOften), provisionMinutes: Number.isFinite(mins) && mins > 0 ? mins : null, status,
    };
    try {
      if (target) await updateTarget(learnerId, target.id, body);
      else await createTarget(learnerId, goalId, body);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
      setBusy(false);
    }
  }

  return (
    <div className="planner-target-editor">
      <h4>{target ? t('Edit target') : t('New target')}</h4>
      <div className="field">
        <label htmlFor={`target-statement-${id}`}>{t('Target')}</label>
        <textarea id={`target-statement-${id}`} rows={3} maxLength={2000} value={statement} onChange={(e) => setStatement(e.target.value)} />
        <p className="field-hint">{t("Specific and measurable, e.g. 'Says help please when stuck, 8 of 10 chances, on 3 different days'")}</p>
      </div>
      <div className="field">
        <label htmlFor={`target-baseline-${id}`}>{t('What happens now')}</label>
        <textarea id={`target-baseline-${id}`} rows={2} maxLength={2000} value={baseline} onChange={(e) => setBaseline(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={`target-success-${id}`}>{t('How we will know')}</label>
        <input id={`target-success-${id}`} type="text" maxLength={1000} value={success} onChange={(e) => setSuccess(e.target.value)} />
        <p className="field-hint">{t('What you will see when the target is met.')}</p>
      </div>
      <div className="planner-goal-fields">
        <div className="field">
          <label htmlFor={`target-start-${id}`}>{t('Starts on')}</label>
          <DateField id={`target-start-${id}`} value={startDate} onChange={(v) => setStartDate(v ?? '')} />
        </div>
        <div className="field">
          <label htmlFor={`target-by-${id}`}>{t('By when')}</label>
          <DateField id={`target-by-${id}`} value={byDate} onChange={(v) => setByDate(v ?? '')} />
        </div>
        <div className="field">
          <label htmlFor={`target-review-${id}`}>{t('Review date')}</label>
          <DateField id={`target-review-${id}`} value={reviewDate} onChange={(v) => setReviewDate(v ?? '')} />
        </div>
        <div className="field">
          <label htmlFor={`target-status-${id}`}>{t('Status')}</label>
          <select id={`target-status-${id}`} value={status} onChange={(e) => setStatus(e.target.value as TargetStatus)}>
            {(Object.keys(TARGET_STATUS_LABEL) as TargetStatus[]).map((s) => <option key={s} value={s}>{t(TARGET_STATUS_LABEL[s])}</option>)}
          </select>
        </div>
      </div>
      <fieldset className="planner-target-provision">
        <legend className="field-label">{t('Provision: the support that teaches it')}</legend>
        <div className="field">
          <label htmlFor={`target-what-${id}`}>{t('What')}</label>
          <input id={`target-what-${id}`} type="text" maxLength={1000} value={what} onChange={(e) => setWhat(e.target.value)} />
        </div>
        <div className="planner-goal-fields">
          <div className="field">
            <label htmlFor={`target-who-${id}`}>{t('Who runs it')}</label>
            <input id={`target-who-${id}`} type="text" maxLength={200} value={who} onChange={(e) => setWho(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor={`target-often-${id}`}>{t('How often')}</label>
            <input id={`target-often-${id}`} type="text" maxLength={200} value={howOften} onChange={(e) => setHowOften(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor={`target-minutes-${id}`}>{t('Minutes per session')}</label>
            <input id={`target-minutes-${id}`} type="number" inputMode="numeric" min={1} max={600} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          </div>
        </div>
      </fieldset>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || !statement.trim()} onClick={() => void save()}>{t('Save target')}</button>
        <button type="button" className="btn secondary" disabled={busy} onClick={onCancel}>{t('Cancel')}</button>
        {onDelete && <button type="button" className="btn quiet-danger planner-goal-delete" disabled={busy} onClick={onDelete}>{t('Delete this target')}</button>}
      </div>
    </div>
  );
}
